import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Home.css";

const heroSlides = [
  { image: "/assets/images/banner1.jpg" },
  { image: "/assets/images/banner2.jpg" },
  { image: "/assets/images/banner3.jpg" },
  { image: "/assets/images/banner4.jpg" },
];

const categories = [
  {
    title: "Lutte contre l'incendie",
    desc: "Extincteurs, robinets d'incendie, systèmes de détection et accessoires de protection.",
    icon: "🔥",
    iconClass: "fire",
    image: "/assets/images/banner1.jpg",
    link: "/incendie",
  },
  {
    title: "Équipements pétroliers",
    desc: "Pompes, compteurs, pistolets de distribution et raccords pétroliers certifiés.",
    icon: "⛽",
    iconClass: "oil",
    image: "/assets/images/banner2.jpg",
    link: "/petroliers",
  },
  {
    title: "Robinetterie",
    desc: "Vannes, clapets, robinets à tournant sphérique et instrumentation industrielle.",
    icon: "🔧",
    iconClass: "valve",
    image: "/assets/images/banner3.jpg",
    link: "/robinetterie",
  },
  {
    title: "Salle de lavage",
    desc: "Équipements de lavage haute pression, enrouleurs et accessoires professionnels.",
    icon: "💧",
    iconClass: "wash",
    image: "/assets/images/banner4.jpg",
    link: "/lavage",
  },
  {
    title: "Hydraulique",
    desc: "Flexibles, raccords, centrales hydrauliques et composants de transmission.",
    icon: "⚙️",
    iconClass: "hydro",
    image: "/assets/images/banner5.jpg",
    link: "/hydraulique",
  },
  {
    title: "Tuyauterie",
    desc: "Tubes, raccords, brides et accessoires de tuyauterie industrielle.",
    icon: "🏗️",
    iconClass: "pipe",
    image: "/assets/images/banner6.jpg",
    link: "/produits",
  },
];

const partners = [
  { src: "/assets/images/p1.webp", alt: "Partenaire 1" },
  { src: "/assets/images/p2.jpg", alt: "Partenaire 2" },
  { src: "/assets/images/p3.png", alt: "Partenaire 3" },
  { src: "/assets/images/p4.jpg", alt: "Partenaire 4" },
  { src: "/assets/images/p5.jpg", alt: "Partenaire 5" },
  { src: "/assets/images/p6.png", alt: "Partenaire 6" },
  { src: "/assets/images/p7.jpg", alt: "Partenaire 7" },
  { src: "/assets/images/p8.jpg", alt: "Partenaire 8" },
  { src: "/assets/images/p9.jpg", alt: "Partenaire 9" },
];

function Home() {
  /* ── Hero background slideshow ── */
  const [activeSlide, setActiveSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setActiveSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  /* ── Partners slider settings ── */
  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 600,
    slidesToShow: 5,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2500,
    pauseOnHover: true,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 4 } },
      { breakpoint: 768, settings: { slidesToShow: 3 } },
      { breakpoint: 480, settings: { slidesToShow: 2 } },
    ],
  };

  return (
    <div className="home-page">
      {/* ═══════ Hero ═══════ */}
      <section className="hero" id="home">
        <div className="hero__slides">
          {heroSlides.map((slide, i) => (
            <div
              key={i}
              className={`hero__slide ${i === activeSlide ? "hero__slide--active" : ""}`}
              style={{ backgroundImage: `url(${slide.image})` }}
            />
          ))}
        </div>
        <div className="hero__overlay" />

        <div className="hero__content">
          <div className="hero__text">
            <div className="hero__badge">
              <span className="hero__badge-dot" />
              Depuis +30 ans d'expertise
            </div>
            <h1 className="hero__title">
              Votre partenaire en <span>équipements industriels</span>
            </h1>
            <p className="hero__subtitle">
              BOUCHADDAKH fournit des solutions complètes en équipements
              hydrauliques, pétroliers et industriels avec un engagement qualité
              inégalé.
            </p>
            <div className="hero__actions">
              <Link to="/produits" className="hero__btn hero__btn--primary">
                Découvrir nos produits →
              </Link>
              <Link to="/contact" className="hero__btn hero__btn--outline">
                Nous contacter
              </Link>
            </div>
          </div>

          <div className="hero__visual">
            <div className="hero__stat-cards">
              <div className="hero__stat-card">
                <div className="hero__stat-number"><span>5000+</span></div>
                <div className="hero__stat-label">Produits</div>
              </div>
              <div className="hero__stat-card">
                <div className="hero__stat-number"><span>30+</span></div>
                <div className="hero__stat-label">Années</div>
              </div>
              <div className="hero__stat-card">
                <div className="hero__stat-number"><span>10+</span></div>
                <div className="hero__stat-label">Partenaires</div>
              </div>
              <div className="hero__stat-card">
                <div className="hero__stat-number"><span>6</span></div>
                <div className="hero__stat-label">Catégories</div>
              </div>
            </div>
          </div>
        </div>

        <div className="hero__scroll">
          Défiler
          <div className="hero__scroll-line" />
        </div>
      </section>

      {/* ═══════ Categories ═══════ */}
      <section className="categories" id="categories">
        <div className="categories__container">
          <div className="section-header">
            <span className="section-label">Nos Produits</span>
            <h2 className="section-title">Des solutions pour chaque besoin</h2>
            <p className="section-desc">
              Explorez notre gamme complète d'équipements industriels,
              hydrauliques et pétroliers.
            </p>
          </div>

          <div className="categories__grid">
            {categories.map((cat, i) => (
              <Link to={cat.link} className="category-card" key={i}>
                <div className="category-card__image">
                  <img src={cat.image} alt={cat.title} />
                </div>
                <div className="category-card__body">
                  <div className={`category-card__icon category-card__icon--${cat.iconClass}`}>
                    {cat.icon}
                  </div>
                  <h3 className="category-card__title">{cat.title}</h3>
                  <p className="category-card__desc">{cat.desc}</p>
                  <span className="category-card__link">
                    Voir les produits →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ About Section ═══════ */}
      <section className="about-section" id="about">
        <div className="about-section__container">
          <div className="about-section__image-wrapper">
            <div className="about-section__image">
              <img src="/assets/images/ans2.png" alt="BOUCHADDAKH" />
            </div>
          </div>

          <div className="about-section__content">
            <span className="section-label">À propos</span>
            <h2 className="section-title">
              Expert en équipements hydrauliques, industriels et pétroliers
            </h2>
            <p className="section-desc" style={{ margin: 0, maxWidth: "100%" }}>
              Depuis de nombreuses années, BOUCHADDAKH s'impose comme un
              partenaire de confiance dans la fourniture d'équipements de qualité
              supérieure.
            </p>

            <Link to="/about" className="hero__btn hero__btn--primary" style={{ marginTop: 32 }}>
              Découvrez-en plus →
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════ Partners ═══════ */}
      <section className="partners" id="partners">
        <div className="partners__container">
          <div className="section-header">
            <span className="section-label">Confiance</span>
            <h2 className="section-title">Nos Partenaires</h2>
            <p className="section-desc">
              Nous collaborons avec les plus grandes marques mondiales pour vous
              offrir le meilleur.
            </p>
          </div>

          <div className="partners__slider">
            <Slider {...sliderSettings}>
              {partners.map((p, i) => (
                <div key={i}>
                  <div className="partner-logo">
                    <img src={p.src} alt={p.alt} />
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </section>

      {/* ═══════ CTA Banner ═══════ */}
      <section className="cta-banner">
        <div className="cta-banner__content">
          <h2 className="cta-banner__title">
            Besoin d'un devis ou d'informations ?
          </h2>
          <p className="cta-banner__desc">
            Notre équipe est à votre disposition pour répondre à toutes vos
            questions et vous accompagner dans vos projets.
          </p>
          <div className="cta-banner__actions">
            <Link to="/contact" className="hero__btn hero__btn--primary">
              Contactez-nous →
            </Link>
            <a href="tel:+21671330366" className="hero__btn hero__btn--outline">
              +(216) 71 330 366
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
